import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDown,
  Factory,
  ClipboardCheck,
  Ship,
  Package,
  Globe2,
  FileCheck2,
  Handshake,
  UserCheck,
  Repeat2,
  Boxes,
  FileText,
  ShieldCheck,
  Tag,
  Container,
  Leaf,
  BadgeCheck,
} from "lucide-react";
import SEO from "../components/SEO";
import Marquee from "../components/Marquee";
import { Reveal, MaskedLines, Stagger, StaggerItem, SectionLabel, EASE } from "../components/Motion";
import { IMAGES, PRODUCTS, COMING_SOON, waLink } from "../lib/site";
import { useLightbox, Watermark } from "../components/Lightbox";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen bg-charcoal overflow-hidden flex flex-col">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.charcoalHero}
          alt="Indonesian coconut shell charcoal briquettes for export"
          className="w-full h-[120%] object-cover img-spotlight"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/80 via-[#111111]/55 to-[#111111]" />
      </motion.div>

      <div className="absolute inset-0 tech-grid pointer-events-none" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex-1 flex flex-col justify-end max-w-[1440px] w-full mx-auto px-5 sm:px-10 pb-16 pt-40"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="w-2 h-2 bg-clay rotate-45" />
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-offwhite/60">
            PT Haqila Global Trading — Indonesia
          </p>
        </motion.div>

        <MaskedLines
          data-testid="home-primary-heading"
          lines={["Indonesian Commodities.", "Sourced for the World."]}
          className="font-display text-[13vw] sm:text-[9vw] lg:text-[7.2vw] font-black tracking-tight leading-[0.98] text-offwhite"
          lineClassName="will-change-transform"
          delay={0.35}
        />

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="max-w-xl"
          >
            <p className="font-display text-lg sm:text-xl font-semibold text-offwhite">
              Direct access to selected Indonesian manufacturers and producers.
            </p>
            <p className="mt-3 text-sm sm:text-base text-offwhite/60 leading-relaxed">
              Coconut shell charcoal briquettes from Indonesia, sourced through selected manufacturing partners.
              We coordinate wholesale charcoal supply for shisha, hookah, BBQ and grilling buyers, from
              specification review to export from Indonesia.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link
              to="/request-a-quote"
              data-testid="hero-quote-cta"
              className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-cta"
              className="inline-flex items-center gap-3 border border-offwhite/30 hover:border-offwhite text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
            >
              Explore Products
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-14 pt-6 border-t border-white/10 flex items-center justify-between"
        >
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-offwhite/45">
            Indonesia — Sourcing — Export — Global Supply
          </p>
          <ArrowDown className="w-4 h-4 text-offwhite/45 animate-bounce" />
        </motion.div>
      </motion.div>
    </section>
  );
}

const CREDIBILITY = [
  "Direct Indonesian Sourcing",
  "Selected Manufacturing Partners",
  "Export Coordination",
  "Product Documentation",
  "Flexible Packaging",
  "International B2B Support",
];

function CredibilityStrip() {
  return (
    <section data-testid="credibility-section" className="bg-offwhite pt-20 sm:pt-24">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50 mb-8">
            Built Around Verified Supply Relationships
          </p>
        </Reveal>
        <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.06}>
          {CREDIBILITY.map((item, i) => (
            <StaggerItem key={item}>
              <div
                data-testid={`credibility-${i + 1}`}
                className="bg-offwhite hover:bg-sand px-5 py-6 h-full flex items-center transition-colors duration-300"
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-charcoal/70 leading-relaxed">
                  {item}
                </span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

const VALUE_PROPS = [
  {
    icon: Factory,
    title: "Direct Manufacturer Access",
    copy: "We build direct relationships with selected manufacturers and producers at the source.",
  },
  {
    icon: ClipboardCheck,
    title: "Specification-Based Sourcing",
    copy: "Products are matched according to buyer requirements, specifications and intended market.",
  },
  {
    icon: Ship,
    title: "Export Coordination",
    copy: "We coordinate sourcing, quotation, documentation and shipment with our supply partners.",
  },
  {
    icon: Package,
    title: "Flexible Supply",
    copy: "Bulk orders, custom packaging and private-label requirements can be discussed based on supplier capability.",
  },
];

function ValueProps() {
  return (
    <section data-testid="value-props-section" className="bg-offwhite py-24 sm:py-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <SectionLabel index="01" title="Value Proposition" />
        <div className="mt-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              From Indonesian Producers to Global Buyers
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 flex items-end">
            <p className="text-base sm:text-lg text-charcoal/65 leading-relaxed">
              We connect international importers, wholesalers and distributors with selected Indonesian manufacturers.
              Our <Link to="/sourcing" data-testid="home-sourcing-context-link" className="text-inherit">Indonesian sourcing</Link> approach
              matches product specifications, volume and packaging to your market.
            </p>
          </Reveal>
        </div>

        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10 border border-charcoal/10">
          {VALUE_PROPS.map((item, i) => (
            <StaggerItem key={item.title} className="h-full">
              <div
                data-testid={`value-card-${i + 1}`}
                className="group h-full bg-offwhite p-8 sm:p-10 flex flex-col gap-6 hover:bg-charcoal transition-colors duration-500"
              >
                <span className="font-display text-xs font-semibold tracking-[0.3em] text-charcoal/35 group-hover:text-offwhite/35 transition-colors duration-500">
                  0{i + 1}
                </span>
                <item.icon className="w-7 h-7 text-forest group-hover:text-clay transition-colors duration-500" />
                <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-offwhite transition-colors duration-500">
                  {item.title}
                </h3>
                <p className="text-sm text-charcoal/60 leading-relaxed group-hover:text-offwhite/60 transition-colors duration-500">
                  {item.copy}
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function ProductFocus() {
  const openLightbox = useLightbox();
  return (
    <section data-testid="product-focus-section" className="bg-offwhite pb-24 sm:pb-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <SectionLabel index="02" title="Current Product Focus" />
        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Our Current Product Focus
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-base sm:text-lg text-charcoal/65 leading-relaxed">
              We supply Indonesian coconut charcoal for shisha and hookah businesses, BBQ and grilling businesses,
              hospitality and food-service buyers. <Link to="/private-label" data-testid="home-private-label-context-link" className="text-inherit">Private label charcoal</Link> options
              can be reviewed for international brands.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-8">
          {PRODUCTS.map((product, i) => (
            <Reveal key={product.slug} delay={i * 0.12}>
              <Link
                to={product.slug}
                data-testid={`product-card-${product.short.toLowerCase().replace(/\s+/g, "-")}`}
                className="group block bg-charcoal text-offwhite overflow-hidden"
              >
                <div className="relative h-72 sm:h-96 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.imageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover img-spotlight transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/85 via-transparent to-transparent" />
                  <span className="absolute top-5 left-5 text-[10px] font-bold uppercase tracking-[0.25em] bg-offwhite/10 backdrop-blur-md border border-white/15 px-3 py-1.5">
                    Export-Ready
                  </span>
                  <button
                    type="button"
                    aria-label={`Enlarge ${product.name} photo`}
                    data-testid={`product-card-zoom-${product.short.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openLightbox([{ src: product.image, alt: product.imageAlt, caption: product.name }], 0);
                    }}
                    className="absolute inset-0 cursor-zoom-in"
                  />
                  <Watermark />
                </div>
                <div className="p-8 sm:p-10">
                  <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{product.name}</h3>
                  <p className="mt-3 text-sm text-offwhite/60 leading-relaxed">{product.description}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-clay group-hover:text-offwhite transition-colors duration-300">
                    View Product
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHY_ITEMS = [
  { icon: Factory, title: "Direct Indonesian Sourcing", copy: "We work directly with selected Indonesian manufacturers and producers." },
  { icon: FileCheck2, title: "Product Transparency", copy: "Product specifications and supporting documents can be provided for buyer review." },
  { icon: Ship, title: "Export Coordination", copy: "From sourcing and quotation to documentation and shipment coordination." },
  { icon: UserCheck, title: "One Point of Contact", copy: "International buyers can manage their Indonesian sourcing through one export partner." },
  { icon: Repeat2, title: "Long-Term Relationships", copy: "We focus on building repeat supply relationships rather than one-off transactions." },
];

function WhySection() {
  return (
    <section data-testid="why-section" className="bg-sand py-24 sm:py-32 tech-grid-light">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <SectionLabel index="03" title="Why HAQILA" />
        <Reveal className="mt-8 max-w-3xl">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
            Why Work With HAQILA?
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10">
          {WHY_ITEMS.map((item, i) => (
            <StaggerItem key={item.title} className={i === 0 ? "lg:row-span-1" : ""}>
              <div
                data-testid={`why-card-${i + 1}`}
                className="h-full bg-sand p-8 sm:p-10 flex flex-col gap-5 hover:bg-offwhite transition-colors duration-500"
              >
                <div className="flex items-center justify-between">
                  <item.icon className="w-6 h-6 text-forest" />
                  <span className="font-display text-xs font-semibold tracking-[0.3em] text-charcoal/30">0{i + 1}</span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight">{item.title}</h3>
                <p className="text-sm text-charcoal/60 leading-relaxed">{item.copy}</p>
              </div>
            </StaggerItem>
          ))}
          <StaggerItem>
            <div className="h-full bg-forest text-offwhite p-8 sm:p-10 flex flex-col justify-between gap-8">
              <p className="font-display text-2xl font-bold tracking-tight leading-snug">
                One export partner. Direct access to Indonesia.
              </p>
              <Link
                to="/why-haqila"
                data-testid="why-more-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-offwhite/80 hover:text-offwhite transition-colors duration-300"
              >
                Learn more <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </StaggerItem>
        </Stagger>
      </div>
    </section>
  );
}

const NETWORK_STEPS = [
  { icon: Globe2, label: "Global Buyer" },
  { icon: Handshake, label: "HAQILA" },
  { icon: Factory, label: "Selected Indonesian Manufacturing Partner" },
  { icon: Boxes, label: "Production" },
  { icon: Ship, label: "Export" },
  { icon: Container, label: "Global Destination" },
];

function NetworkDiagram() {
  return (
    <section data-testid="network-section" className="bg-charcoal text-offwhite py-24 sm:py-32 tech-grid">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <SectionLabel index="04" title="Manufacturing Network" dark />
        <div className="mt-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-6">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Direct Access to Selected Indonesian Manufacturers
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 flex items-end">
            <p className="text-base sm:text-lg text-offwhite/60 leading-relaxed">
              HAQILA works closely with selected manufacturing partners across Indonesia. Our role is to understand
              buyer requirements, identify suitable production sources, verify product information and coordinate
              commercial and export requirements.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 overflow-x-auto pb-4" data-testid="network-diagram">
          <Stagger className="flex items-stretch gap-0 min-w-[900px]" gap={0.12}>
            {NETWORK_STEPS.map((step, i) => (
              <StaggerItem key={step.label} className="flex items-center">
                <div
                  className={`flex flex-col items-center text-center gap-4 px-6 py-8 border w-[180px] ${
                    step.label === "HAQILA"
                      ? "bg-forest border-forest"
                      : "bg-white/[0.03] border-white/12"
                  }`}
                >
                  <step.icon className={`w-7 h-7 ${step.label === "HAQILA" ? "text-offwhite" : "text-clay"}`} />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] leading-relaxed">{step.label}</span>
                </div>
                {i < NETWORK_STEPS.length - 1 && (
                  <div className="flex items-center px-1">
                    <span className="w-8 border-t border-dashed border-white/30" />
                    <span className="w-1.5 h-1.5 rotate-45 bg-clay -ml-1" />
                  </div>
                )}
              </StaggerItem>
            ))}
          </Stagger>
        </div>

        <Reveal className="mt-14">
          <Link
            to="/request-a-quote"
            data-testid="network-cta"
            className="group inline-flex items-center gap-3 bg-offwhite text-charcoal hover:bg-clay hover:text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Discuss Your Sourcing Requirements
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export const PROCESS_STEPS = [
  { title: "Buyer Inquiry", copy: "You share the product, specification, quantity and destination you need." },
  { title: "Requirement Review", copy: "We review your requirements and clarify technical and commercial details." },
  { title: "Supplier Matching", copy: "We identify suitable manufacturing partners for your specification." },
  { title: "Sample / COA", copy: "Samples and available product documentation can be arranged for review." },
  { title: "Quotation", copy: "A commercial quotation is prepared based on confirmed requirements." },
  { title: "Production", copy: "The selected manufacturing partner produces according to the agreed specification." },
  { title: "Quality & Documentation", copy: "Product checks and export documentation are coordinated before container loading." },
  { title: "Shipment", copy: "Container loading and shipment to your destination port are coordinated." },
];

function ProcessSection() {
  return (
    <section data-testid="process-section" className="bg-offwhite py-24 sm:py-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <SectionLabel index="05" title="Sourcing Process" />
        <Reveal className="mt-8 max-w-3xl">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
            From Inquiry to Shipment
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.06}>
          {PROCESS_STEPS.map((step, i) => (
            <StaggerItem key={step.title}>
              <div
                data-testid={`process-step-${i + 1}`}
                className="group h-full bg-offwhite p-8 flex flex-col gap-6 hover:bg-charcoal transition-colors duration-500"
              >
                <span className="font-display text-5xl font-black tracking-tight text-outline-dark group-hover:text-clay group-hover:[-webkit-text-stroke:0px] transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-lg font-bold tracking-tight group-hover:text-offwhite transition-colors duration-500">
                  {step.title}
                </h3>
                <p className="text-sm text-charcoal/60 leading-relaxed group-hover:text-offwhite/60 transition-colors duration-500">
                  {step.copy}
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

const EXPORT_READY = [
  { icon: FileText, label: "Product specifications" },
  { icon: FileCheck2, label: "COA availability" },
  { icon: ShieldCheck, label: "MSDS where applicable" },
  { icon: Package, label: "Packaging options" },
  { icon: Boxes, label: "Bulk orders" },
  { icon: Tag, label: "Private label opportunities" },
  { icon: ClipboardCheck, label: "FOB / CIF quotation" },
  { icon: Ship, label: "Shipment coordination" },
];

function ExportReady() {
  const openLightbox = useLightbox();
  return (
    <section data-testid="export-ready-section" className="bg-charcoal text-offwhite py-24 sm:py-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionLabel index="06" title="Export Ready" dark />
          <Reveal className="mt-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.02]">
              Built Around International Buyer Requirements
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-base text-offwhite/60 leading-relaxed">
              Available upon request and subject to product/supplier documentation.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10">
            <div
              className="relative h-64 sm:h-80 overflow-hidden cursor-zoom-in"
              onClick={() =>
                openLightbox(
                  [{ src: IMAGES.containers, alt: "Export containers at port", caption: "Export Logistics — Indonesia" }],
                  0
                )
              }
            >
              <img
                src={IMAGES.containers}
                alt="Export containers at port"
                loading="lazy"
                className="w-full h-full object-cover img-spotlight"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 to-transparent" />
              <span className="absolute bottom-4 left-4 text-[10px] font-bold uppercase tracking-[0.25em] text-offwhite/70">
                Export Logistics — Indonesia
              </span>
              <Watermark />
            </div>
          </Reveal>
        </div>
        <Stagger className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 self-center" gap={0.05}>
          {EXPORT_READY.map((item, i) => (
            <StaggerItem key={item.label}>
              <div
                data-testid={`export-item-${i + 1}`}
                className="bg-charcoal p-6 sm:p-8 flex items-center gap-4 hover:bg-white/[0.04] transition-colors duration-300"
              >
                <item.icon className="w-5 h-5 text-clay shrink-0" />
                <span className="text-sm font-medium tracking-wide">{item.label}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function ComingSoon() {
  return (
    <section data-testid="coming-soon-section" className="bg-offwhite py-24 sm:py-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <SectionLabel index="07" title="Coming Soon" />
        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Expanding Indonesian Commodity Supply
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-base sm:text-lg text-charcoal/65 leading-relaxed">
              Our product portfolio is gradually expanding through relationships with Indonesian producers and
              suppliers.
            </p>
          </Reveal>
        </div>
        <Stagger className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.07}>
          {COMING_SOON.map((name) => (
            <StaggerItem key={name}>
              <div
                data-testid={`coming-soon-${name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className="bg-offwhite p-6 sm:p-8 h-full flex flex-col justify-between gap-10 min-h-[170px]"
              >
                <span className="self-start text-[10px] font-bold uppercase tracking-[0.25em] text-clay border border-clay/40 px-2.5 py-1">
                  Coming Soon
                </span>
                <h3 className="font-display text-lg font-bold tracking-tight text-charcoal/70">{name}</h3>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function FinalCta() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="final-cta-section" className="relative bg-charcoal text-offwhite py-28 sm:py-40 overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.coconutShell}
          alt="Raw coconut shells — Indonesian sourcing origin"
          loading="lazy"
          className="w-full h-[125%] object-cover img-spotlight opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/70 to-charcoal" />
      </motion.div>
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-10">
        <MaskedLines
          lines={["Looking for", "Indonesian Products?"]}
          as="h2"
          data-testid="home-final-cta-heading"
          className="font-display text-5xl sm:text-7xl font-black tracking-tight leading-[0.98]"
          delay={0.1}
        />
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-offwhite/65 leading-relaxed">
            Tell us what you need. We'll review your product, specification, quantity and destination requirements and
            help identify suitable sourcing options from Indonesia.
          </p>
        </Reveal>
        <Reveal delay={0.55} className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/request-a-quote"
            data-testid="final-quote-cta"
            className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Request a Quote
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-contact-cta"
            className="inline-flex items-center gap-3 border border-offwhite/30 hover:border-offwhite text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Quote via WhatsApp
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function SupplyChainVideo() {
  return (
    <section data-testid="supply-chain-video-section" className="bg-offwhite py-24 sm:py-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Inside Our Supply Chain
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-4 text-base sm:text-lg text-charcoal/65 leading-relaxed max-w-xl mx-auto">
            From partner kilns in Java to your port of destination.
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-12 max-w-[960px] mx-auto">
          <div className="border-2 border-clay p-2 sm:p-3">
            <div className="relative aspect-video overflow-hidden">
              <iframe
                src="https://www.youtube-nocookie.com/embed/_VO4tCykqsw?rel=0&modestbranding=1"
                title="HAQILA Global Trading - Coconut Shell Charcoal Supplier Indonesia"
                data-testid="supply-chain-video-embed"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                loading="lazy"
                allowFullScreen
                allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
              />
            </div>
          </div>
          <p data-testid="supply-chain-partner-capacity" className="mt-4 text-xs sm:text-sm text-charcoal/45">
            Selected Partner Facility — Banten
            <br />
            Production capacity: up to 26 MT/day, subject to production schedule and confirmed order requirements.
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/request-a-quote"
              data-testid="supply-chain-video-cta"
              className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TrustBadgeStrip() {
  const badges = [
    { type: "icon", Icon: Leaf, label: "Coconut Shell Charcoal-Based", sub: "Produced from coconut shell charcoal." },
    {
      type: "crop",
      src: IMAGES.msdsLogo,
      // native 1959x803, diamond mark bbox (90,0)-(875,590), scaled to a 64px-tall crop
      crop: { boxW: 85, boxH: 64, imgW: 213, imgH: 87, offsetX: -9.76, offsetY: 0 },
      label: "MSDS Available",
      sub: "Safety Data Sheet on request",
    },
    {
      type: "logo",
      src: IMAGES.carsurinLogo,
      logoHeight: 64,
      plate: false,
      label: "Independently Lab Tested",
      sub: "KAN-accredited ISO/IEC 17025",
      link: "/documentation",
    },
    {
      type: "logo",
      src: IMAGES.ministryOfTradeLogo,
      logoHeight: 40,
      plate: true,
      label: "Registered Indonesian Company",
      sub: "PT HAQILA GLOBAL TRADING",
    },
  ];

  return (
    <section data-testid="trust-badge-section" className="bg-offwhite py-16 sm:py-20">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <Reveal>
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.3em] text-charcoal/40">
            Quality &amp; Compliance
          </p>
        </Reveal>
        <Stagger className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" gap={0.07}>
          {badges.map((b, i) => {
            const inner = (
              <div
                data-testid={`trust-badge-${i + 1}`}
                className="h-full bg-charcoal border border-clay p-6 flex flex-col items-center text-center gap-3"
              >
                {b.type === "icon" && <b.Icon className="w-7 h-7 text-clay" strokeWidth={1.5} />}
                {b.type === "crop" && (
                  <div className="overflow-hidden relative" style={{ width: b.crop.boxW, height: b.crop.boxH }}>
                    <img
                      src={b.src}
                      alt={b.label}
                      style={{
                        position: "absolute",
                        top: b.crop.offsetY,
                        left: b.crop.offsetX,
                        width: b.crop.imgW,
                        height: b.crop.imgH,
                        maxWidth: "none",
                        maxHeight: "none",
                      }}
                    />
                  </div>
                )}
                {b.type === "logo" &&
                  (b.plate ? (
                    <div className="bg-offwhite px-4 py-2 flex items-center justify-center">
                      <img src={b.src} alt={b.label} className="object-contain" style={{ height: b.logoHeight }} />
                    </div>
                  ) : (
                    <img src={b.src} alt={b.label} className="object-contain" style={{ height: b.logoHeight }} />
                  ))}
                <div>
                  <p className="text-sm font-bold text-offwhite">{b.label}</p>
                  <p className="mt-1 text-xs text-offwhite/50 leading-snug">{b.sub}</p>
                </div>
              </div>
            );
            return (
              <StaggerItem key={b.label}>
                {b.link ? (
                  <Link to={b.link} data-testid={`trust-badge-link-${i + 1}`} className="block h-full">
                    {inner}
                  </Link>
                ) : (
                  inner
                )}
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

function ExportMarketsStrip() {
  const markets = [
    { name: "United Arab Emirates", flag: "https://flagcdn.com/w80/ae.png" },
    { name: "Saudi Arabia", flag: "https://flagcdn.com/w80/sa.png" },
    { name: "Turkey", flag: "https://flagcdn.com/w80/tr.png" },
    { name: "Egypt", flag: "https://flagcdn.com/w80/eg.png" },
    { name: "Jordan", flag: "https://flagcdn.com/w80/jo.png" },
    { name: "Germany", flag: "https://flagcdn.com/w80/de.png" },
    { name: "Netherlands", flag: "https://flagcdn.com/w80/nl.png" },
    { name: "United States", flag: "https://flagcdn.com/w80/us.png" },
    { name: "South Korea", flag: "https://flagcdn.com/w80/kr.png" },
    { name: "Australia", flag: "https://flagcdn.com/w80/au.png" },
  ];

  return (
    <section data-testid="export-markets-section" className="bg-charcoal py-16 sm:py-20">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 text-center">
        <Reveal>
          <h2 data-testid="export-destinations-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-offwhite">
            Export Destinations
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p data-testid="export-destinations-note" className="mt-4 text-base text-offwhite/55 leading-relaxed max-w-xl mx-auto">
            Destination requirements are reviewed according to product, documentation and buyer requirements.
          </p>
        </Reveal>
        <Stagger className="mt-10 grid grid-cols-5 sm:grid-cols-10 gap-4 sm:gap-6" gap={0.04}>
          {markets.map((m) => (
            <StaggerItem key={m.name}>
              <div data-testid={`export-market-${m.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex flex-col items-center gap-2">
                <img
                  src={m.flag}
                  alt={`${m.name} flag`}
                  loading="lazy"
                  width={48}
                  className="w-12 h-auto border border-clay rounded-none"
                />
                <span className="text-[10px] sm:text-xs text-offwhite/50 leading-snug">{m.name}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.2}>
          <p className="mt-8 text-xs text-offwhite/40 italic">Other destinations on request.</p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO />
      <Hero />
      <SupplyChainVideo />
      <TrustBadgeStrip />
      <ExportMarketsStrip />
      <Marquee />
      <CredibilityStrip />
      <ValueProps />
      <ProductFocus />
      <WhySection />
      <NetworkDiagram />
      <ProcessSection />
      <ExportReady />
      <ComingSoon />
      <FinalCta />
    </>
  );
}
